'use client'

import React, { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import useEmblaCarousel from 'embla-carousel-react'
import type { EmblaOptionsType } from 'embla-carousel'
import { TopCarouselProps, CarouselItem, CarouselItemType } from '@/types/carousel'
import { Story } from '@/types/story'
import ArticleCard from '@/components/Shared/Card/ArticleCard'
import styles from './styles.module.scss'
import clsx from 'clsx'

export default function TopCarousel({ items, type, className }: TopCarouselProps) {
  const isArticle = type === 'article'

  const emblaOptions: EmblaOptionsType = {
    loop: true,
    align: isArticle ? 'center' : 'start',
    slidesToScroll: 1,
    dragFree: false,
    containScroll: 'trimSnaps',
    duration: 20,
    skipSnaps: false,
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions)
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setPrevBtnEnabled(emblaApi.canScrollPrev())
    setNextBtnEnabled(emblaApi.canScrollNext())
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  const renderSlideContent = (item: CarouselItemType) => {
    if (isArticle) {
      return <ArticleCard article={item as Story} used='top-carousel' />
    } else {
      const companyItem = item as CarouselItem
      return (
        <Link href={`/${companyItem.openUrl}`} className={styles.cardImgLink}>
          <img
            src={companyItem.imageUrl || '/images/svg/dummy_logo.svg'}
            width={64}
            height={64}
            className={styles.logoImage}
            alt={companyItem.name || 'Company logo'}
            loading='lazy'
          />
        </Link>
      )
    }
  }

  return (
    <div className={clsx(styles.topCarouselWrapper, styles[type], className)}>
      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {items.map((item, index) => (
            <div
              key={item.id || index}
              className={clsx(styles.emblaSlide, styles.carouselSlideItem)}
            >
              {renderSlideContent(item)}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation buttons for article type */}
      {isArticle && (
        <div className={styles.swiperButtonWrapper}>
          <button
            className={clsx(styles.swiperButton, styles.swiperButtonPrev, {
              [styles.disabled]: !prevBtnEnabled,
            })}
            onClick={scrollPrev}
            disabled={!prevBtnEnabled}
            aria-label='Previous'
          />
          <button
            className={clsx(styles.swiperButton, styles.swiperButtonNext, {
              [styles.disabled]: !nextBtnEnabled,
            })}
            onClick={scrollNext}
            disabled={!nextBtnEnabled}
            aria-label='Next'
          />
        </div>
      )}

      {/* Pagination for article type */}
      {isArticle && (
        <div className={styles.swiperPagination}>
          {items.map((_, index) => (
            <button
              key={index}
              className={clsx(styles.paginationBullet, {
                [styles.paginationBulletActive]: selectedIndex === index,
              })}
              onClick={() => emblaApi?.scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
